import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { xorBy } from 'lodash';
import { type SyntheticEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import type { Organization } from '@/pages/Organizations';

import useAnnouncements from '../Announcement.hooks';
import { isModalOpenAtom, modalTypeAtom } from '../Announcements.store';
import type {
  Announcement,
  AnnouncementOrganizationType
} from '../Announcements.types';
import {
  AnnouncementDialogType,
  CREATE_ANNOUNCEMENT_DIALOG_FORM_DEFAULT_VALUES,
  ORG_GROUP_OPTIONS,
  SERVICE_GROUPS
} from './AnnouncementDialog.constants';
import { getGroupSelectedItems } from './AnnouncementDialog.utils';

// Note on autocomplete options:
// All group options should have large ids to avoid conflicts with organization ids
// Otherwise they will be inappropriately highlighted in the dropdown

const useAnnouncementDialog = () => {
  const form = useForm({
    defaultValues: CREATE_ANNOUNCEMENT_DIALOG_FORM_DEFAULT_VALUES
  });
  const { setValue, reset } = form;
  const [type, setType] = useAtom(modalTypeAtom);
  const [open, setOpen] = useAtom(isModalOpenAtom);
  const [industries, setIndustries] = useState<AnnouncementOrganizationType[]>(
    []
  );
  const [orgGroups, setOrgGroups] =
    useState<AnnouncementOrganizationType[]>(ORG_GROUP_OPTIONS);
  const [selectedOptions, setSelectedOptions] = useState<
    AnnouncementOrganizationType[]
  >([]);

  const {
    organizations,
    selectedAnnouncement,
    selectedGroupList,
    setSelectedAnnouncement,
    setSelectedGroupList
  } = useAnnouncements();

  useEffect(() => {
    const uniqueIndustries: AnnouncementOrganizationType[] = [];
    const seenIndustries = new Set(); // Set to track already added industries

    organizations.forEach((org: Organization, index: number) => {
      if (org.industry && !seenIndustries.has(org.industry)) {
        // If the industry is not already in the set, add it
        uniqueIndustries.push({
          id: 60000 + index,
          name: org.industry,
          group: 'Industry',
          label: org.industry,
          selected: false
        });
        seenIndustries.add(org.industry); // Add to set to track this industry
      }
    });
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIndustries(uniqueIndustries);
  }, [organizations]);

  // Reset form values creating new announcement
  useEffect(() => {
    const selectedOrgs: AnnouncementOrganizationType[] = [];
    if (type === AnnouncementDialogType.CREATE) {
      reset(CREATE_ANNOUNCEMENT_DIALOG_FORM_DEFAULT_VALUES);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedOptions([]);
      setSelectedGroupList([]);
      setSelectedAnnouncement({} as Announcement);
    } else if (selectedAnnouncement.id) {
      const parsedOrgs = selectedAnnouncement.org_list
        ? JSON.parse(selectedAnnouncement.org_list as string)
        : [];
      if (
        selectedAnnouncement.org_list !== '' &&
        parsedOrgs.length === organizations.length
      ) {
        selectedOrgs.push({
          id: 100000,
          name: 'All Organizations',
          group: 'Select All',
          label: 'ALL',
          selected: false
        } as AnnouncementOrganizationType);
      } else {
        organizations.forEach((org: Organization) => {
          if (parsedOrgs.includes(org.label)) {
            selectedOrgs.push(org as AnnouncementOrganizationType);
          }
        });
      }
      // Selected orgs are saved to form before submitting, here we are setting the selected orgs to be displayed
      setSelectedOptions(selectedOrgs);

      setValue('title', selectedAnnouncement.title);
      setValue('body', selectedAnnouncement.body);
      setValue('send_email', selectedAnnouncement.send_email);
      setValue('attachments', selectedAnnouncement.attachments);
      setValue(
        'scheduled_at',
        dayjs.utc(selectedAnnouncement.scheduled_at).local()
      );
    }
  }, [type]);

  const handleOrgSelectChange = async (
    event: SyntheticEvent<Element, Event>,
    selectedItems: AnnouncementOrganizationType[],
    reason: string
  ) => {
    // Handle clear all orgs
    if (reason === 'clear') {
      // Reset selected orgs
      setSelectedOptions([]);

      // Reset selected groups so all groups are unselected
      setOrgGroups((prevGroups: AnnouncementOrganizationType[]) =>
        prevGroups.map((group: AnnouncementOrganizationType) => ({
          ...group,
          selected: false
        }))
      );

      // Reset selected industries so all industries are unselected
      setIndustries((prevIndustries: AnnouncementOrganizationType[]) =>
        prevIndustries.map((industry: AnnouncementOrganizationType) => ({
          ...industry,
          selected: false
        }))
      );

      // Reset org_list form value
      setValue('org_list', '');
      return;
    }

    // Get the last clicked item (whether selected or deselected)
    const lastClicked = xorBy(selectedItems, selectedOptions, 'id')[0];

    // Handle remove orgs when clicking on x button on org chip only
    // Deselects all groups impacted by removing the org
    if (reason === 'removeOption' && !(event.target as HTMLElement).outerText) {
      // Get removed item industry and groups
      const removedItemIndustry = lastClicked?.industry || '';
      const removedItemServiceGroups = selectedGroupList.filter((item) =>
        lastClicked?.product_type?.includes(item)
      );

      // If item has any service groups that are selected, deselect them
      if (removedItemServiceGroups.length > 0) {
        // Set removed groups to unselected
        setOrgGroups((prevGroups: AnnouncementOrganizationType[]) => {
          const updatedGroups = [...prevGroups];
          removedItemServiceGroups.forEach((group: string) => {
            const groupIndex = updatedGroups.findIndex(
              (groupItem: AnnouncementOrganizationType) =>
                groupItem.label === group
            );
            if (groupIndex !== -1) {
              updatedGroups[groupIndex].selected = false;
            }
          });
          return updatedGroups;
        });

        // Remove item from selectedGroupList
        setSelectedGroupList((prevList: string[]) => {
          const newList = prevList.filter(
            (item) => !removedItemServiceGroups.includes(item)
          );
          return newList;
        });
      }

      // Index of industry in selected group list
      const industrySelectedGroupIndex =
        selectedGroupList.indexOf(removedItemIndustry);

      // If industry is selected, deselect it
      if (industrySelectedGroupIndex !== -1) {
        // Remove item from selectedGroupList
        setSelectedGroupList((prevList: string[]) => {
          prevList.splice(industrySelectedGroupIndex, 1);
          return prevList;
        });

        // If industry is deselected, update selectedOptions
        setIndustries((prevGroups: AnnouncementOrganizationType[]) => {
          const updatedGroups = [...prevGroups];
          const industryIndex = industries.findIndex(
            (industry) => industry.name === removedItemIndustry
          );
          updatedGroups[industryIndex].selected = false;
          return updatedGroups;
        });
      }

      setSelectedOptions(selectedItems);
      return;
    }

    // Get the index of the last clicked group
    const selectedGroupIndex = orgGroups.findIndex(
      (group: AnnouncementOrganizationType) => group.name === lastClicked.name
    );

    // Handle service group clicks
    if (SERVICE_GROUPS.includes(lastClicked.name)) {
      // If service group is selected, update selectedOptions
      if (
        !orgGroups[selectedGroupIndex].selected &&
        reason === 'selectOption'
      ) {
        // Add item to selectedGroupList
        setSelectedGroupList((prevList: string[]) => {
          const newList = [...prevList, orgGroups[selectedGroupIndex].label];
          return newList;
        });

        // Update selected orgs that have selected service
        setOrgGroups((prevGroups: AnnouncementOrganizationType[]) => {
          const updatedGroups = [...prevGroups];
          updatedGroups[selectedGroupIndex].selected = true;
          return updatedGroups;
        });

        const newSelectedOptions = getGroupSelectedItems(
          selectedItems,
          selectedGroupIndex,
          'product_type',
          organizations,
          orgGroups
        );

        // Set selectedOptions
        setSelectedOptions(newSelectedOptions);
        return;
      }
      // If service group is deselected by clicking on option in dropdown menu, update selectedOptions
      if (orgGroups[selectedGroupIndex].selected) {
        // Index of selected group in selectedGroupList
        const groupIndex = selectedGroupList.indexOf(
          orgGroups[selectedGroupIndex].label
        );

        // Remove item from selectedGroupList
        setSelectedGroupList((prevList: string[]) => {
          prevList.splice(groupIndex, 1);
          return prevList;
        });

        // If service group is deselected, update selectedOptions
        setOrgGroups((prevGroups: AnnouncementOrganizationType[]) => {
          const updatedGroups = [...prevGroups];
          updatedGroups[selectedGroupIndex].selected = false;
          return updatedGroups;
        });

        // Remove orgs that have no product types in selectedGroupList
        setSelectedOptions((prevOptions: AnnouncementOrganizationType[]) => {
          return prevOptions.filter(
            (org: AnnouncementOrganizationType) =>
              org.product_type &&
              (!org.product_type.includes(
                orgGroups[selectedGroupIndex].label
              ) ||
                org.product_type.some((product: string) =>
                  selectedGroupList.includes(product)
                ))
          );
        });
        return;
      }
    }

    // Handles Industry clicks
    if (industries.some((industry) => industry.name === lastClicked.name)) {
      // Get the index of the last clicked industry
      const industryIndex = industries.findIndex(
        (industry) => industry.name === lastClicked.name
      );

      // If industry is selected, update selectedOptions
      if (!industries[industryIndex].selected) {
        // Add item to selectedGroupList
        setSelectedGroupList((prevList: string[]) => {
          const newList = [...prevList, lastClicked.name];
          return newList;
        });

        // Update industries list to mark industry as selected
        setIndustries((prevGroups: AnnouncementOrganizationType[]) => {
          const updatedGroups = [...prevGroups];
          updatedGroups[industryIndex].selected = true;
          return updatedGroups;
        });

        const newSelectedOptions = getGroupSelectedItems(
          selectedItems,
          industryIndex,
          'industry',
          organizations,
          industries as AnnouncementOrganizationType[]
        );

        // Set selectedOptions
        setSelectedOptions(newSelectedOptions);
        return;
      }

      // If industry is deselected by clicking on option in dropdown menu, update selectedOptions
      if (industries[industryIndex].selected) {
        // Index of selected group in selectedGroupList
        const groupIndex = selectedGroupList.indexOf(
          industries[industryIndex].name
        );

        // Remove item from selectedGroupList
        setSelectedGroupList((prevList: string[]) => {
          prevList.splice(groupIndex, 1);
          return prevList;
        });

        // If service group is deselected, update selectedOptions
        setIndustries((prevGroups: AnnouncementOrganizationType[]) => {
          const updatedGroups = [...prevGroups];
          updatedGroups[industryIndex].selected = false;
          return updatedGroups;
        });

        // Remove orgs that have no product types in selectedGroupList
        setSelectedOptions((prevOptions: AnnouncementOrganizationType[]) => {
          return prevOptions.filter(
            (org: AnnouncementOrganizationType) =>
              org.industry !== industries[industryIndex].name
          );
        });
        return;
      }
    }

    // Handles All Organizations clicks
    if (lastClicked.name === 'All Organizations' && reason === 'selectOption') {
      setSelectedOptions([...selectedItems]);
    }

    // If regular org is selected, update selectedOptions
    setSelectedOptions(selectedItems);
  };

  return {
    handleOrgSelectChange,
    form,
    open,
    orgGroups,
    selectedOptions,
    setOpen,
    setOrgGroups,
    setSelectedOptions,
    setType,
    type,
    industries
  };
};

export default useAnnouncementDialog;
